/**
 * AuthErrorGate — app-level surface for 401/403 responses.
 *
 * When any API call fails with "not signed in" or "not allowed" while the user
 * is navigating, we replace the broken view with one friendly, branded screen
 * and a clear next action instead of leaving empty panels or raw toasts.
 * The gate clears itself whenever the route changes so users are never stuck.
 */

import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, LifeBuoy, LogIn, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorScreen } from "@/components/system/ErrorScreen";
import { onAuthError, shouldSuppressAuthErrorGate, type AuthErrorKind } from "@/lib/api/authErrors";
import { buildSignInPath } from "@/lib/authRedirect";
import { useAuth } from "@/contexts/AuthContext";

interface AuthErrorGateProps {
  children: ReactNode;
}

export function AuthErrorGate({ children }: AuthErrorGateProps) {
  const [kind, setKind] = useState<AuthErrorKind | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(
    () =>
      onAuthError((event) => {
        // Anonymous visitors on public marketing pages can legitimately hit
        // permission-scoped data; those routes handle their own empty states.
        const isAppSurface = event.path.startsWith("/app");
        if (!isAuthenticated && !isAppSurface) return;
        if (shouldSuppressAuthErrorGate(event)) return;
        setKind(event.kind);
      }),
    [isAuthenticated],
  );

  // Any navigation clears the blocked state so the user can keep moving.
  useEffect(() => {
    setKind(null);
  }, [location.pathname, location.search]);

  if (!kind) return <>{children}</>;

  if (kind === "unauthenticated") {
    return (
      <ErrorScreen
        code="401"
        eyebrow="Session ended"
        title="You've been signed out"
        description="Your session expired or was signed out on another device. Sign in again and we'll bring you right back to this page."
        actions={
          <>
            <Button onClick={() => navigate(buildSignInPath(location))} className="gap-2">
              <LogIn className="h-4 w-4" aria-hidden />
              Sign in to continue
            </Button>
            <Button variant="outline" onClick={() => setKind(null)}>
              Try again
            </Button>
          </>
        }
        links={[
          {
            label: "Browse pathways",
            description: "Explore what you can learn on GG Life",
            href: "/learn",
            icon: <BookOpen className="h-4 w-4" aria-hidden />,
          },
          {
            label: "Home",
            description: "Back to the start",
            href: "/",
            icon: <Home className="h-4 w-4" aria-hidden />,
          },
        ]}
      />
    );
  }

  return (
    <ErrorScreen
      code="403"
      eyebrow="Access restricted"
      title="You don't have access to this"
      description="Your account is signed in, but it isn't allowed to view this page or data. If you think that's wrong, our team can check your access."
      actions={
        <>
          <Button onClick={() => navigate("/app/home")} className="gap-2">
            <Home className="h-4 w-4" aria-hidden />
            Go to my dashboard
          </Button>
          <Button variant="outline" onClick={() => setKind(null)}>
            Try again
          </Button>
        </>
      }
      links={[
        {
          label: "Contact support",
          description: "Ask us to review your access",
          href: "/app/support",
          icon: <LifeBuoy className="h-4 w-4" aria-hidden />,
        },
        {
          label: "Browse pathways",
          description: "Content that's open to you",
          href: "/learn",
          icon: <BookOpen className="h-4 w-4" aria-hidden />,
        },
      ]}
    />
  );
}

export default AuthErrorGate;
